import * as R from "ramda";
import * as React from "react";
import Dropzone from "react-dropzone";
import { FaPlus, FaTrash } from "react-icons/fa";
import { connect } from "react-redux";
import { compose, Dispatch } from "redux";
import { IAction } from "reduxsauce";

import { Creators as AllCreators } from "../../../../actions";
import { patchCourseDeleteImmersiveBackgroundImage } from "../../../../actions/course-author/common";
import { FieldSet, FooterNote, InputLink } from "../../../../App/Common/Components/FormComponents";
import { SectionTitle } from "../../../../App/Common/Styles/containers";
import {
  allowedTypes,
  allowedTypesFormats,
  BackgroundPreview,
  ButtonsRow,
  DropDownImage,
  DropDownMessage,
  ErrorMsg,
  FixedAddButton,
  FixedDeleteButton,
  HandleImageContainer,
  PreviewImageContainer,
} from "../../../../App/Common/Styles/DNDImage";
import { IState } from "../../../../App/reducer";
import { IFullCourse } from "../../../course-author/types";
import { FormContainer, Input } from "../FormElements";

const Creators = (AllCreators as any).App.CourseAuthor;

export interface IEditImmersiveBackgroundDispatchProps {
  addImmersiveBackground: (files: File[] | null, link: string | null) => void;
  deleteImmersiveBackground: () => void;
}

export interface IEditImmersiveBackgroundOwnProps {
  course: IFullCourse;
  courseId: string;
}

interface IEditImmersiveBackgroundStateProps {
  updateCourseRequestError: string | null;
}

export type IEditImmersiveBackgroundProps = IEditImmersiveBackgroundOwnProps &
  IEditImmersiveBackgroundStateProps &
  IEditImmersiveBackgroundDispatchProps;

export const EditImmersiveBackground: React.FC<IEditImmersiveBackgroundProps> = props => {
  const linkInputName = "background-img-url";
  const fileInputName = "background-file-upload";
  const [link, setLink] = React.useState<string | null>(null);
  const { course } = props;

  const onLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => setLink(e.target.value);

  const uploadFile = () => (files: File[] | undefined) => {
    if (!files) return;

    if (files.length > 1) {
      alert("Make sure to upload only one image");
      return;
    }

    if (!R.isNil(files) && files.length > 0 && !allowedTypes.includes(files[0].type)) {
      alert(`File type must be one of these: ${allowedTypesFormats}`);
      return;
    }

    if (!R.isNil(files) || !R.isNil(link)) {
      props.addImmersiveBackground(files, link);
    }
  };
  const canDeleteImage = !!course.state.background && !!course.state.style;
  const onDeleteFile = () => {
    if (canDeleteImage && window.confirm("Are you sure you want to delete the current background image?")) {
      props.deleteImmersiveBackground();
    }
  };

  const handleOnDrop = (files: File[]) => uploadFile()(files);

  return (
    <>
      <SectionTitle>Apply Immersive Background</SectionTitle>
      <FormContainer>
        <HandleImageContainer>
          <Dropzone onDrop={handleOnDrop}>
            {({ getRootProps, getInputProps }) => (
              <div style={{ width: "18rem" }} {...getRootProps()}>
                <DropDownImage>
                  <DropDownMessage>Drop image file here or enter URL below</DropDownMessage>
                </DropDownImage>
                <Input
                  {...getInputProps()}
                  data-testid="image-uploader"
                  name={fileInputName}
                  type="file"
                  style={{ display: "none" }}
                />
              </div>
            )}
          </Dropzone>

          {!!props.course.state.background && (
            <PreviewImageContainer>
              <BackgroundPreview alt="Background Image Preview" src={props.course.state.background} />
            </PreviewImageContainer>
          )}
        </HandleImageContainer>
        <FieldSet>
          <legend>Choose image URL</legend>
          <InputLink name={linkInputName} placeholder={"Type here"} onChange={onLinkChange} />
        </FieldSet>
        {props.updateCourseRequestError && <ErrorMsg>{props.updateCourseRequestError}</ErrorMsg>}
        <ButtonsRow width={38} margin={1}>
          <FixedAddButton onClick={uploadFile}>
            <FaPlus /> Add
          </FixedAddButton>
          <FixedDeleteButton onClick={onDeleteFile} disabled={!canDeleteImage}>
            <FaTrash /> Delete background image
          </FixedDeleteButton>
        </ButtonsRow>
        <FooterNote>* Supported files: {allowedTypes}</FooterNote>
      </FormContainer>
    </>
  );
};

export default compose(
  connect(
    (state: IState) => ({
      updateCourseRequestError: state.App.CourseAuthor._.updateCourseRequestError,
    }),
    (dispatch: Dispatch<IAction>, ownProps: IEditImmersiveBackgroundOwnProps) => ({
      addImmersiveBackground: (files: File[] | null, link: string | null) =>
        dispatch(Creators.addImmersiveBackground(ownProps.courseId, ownProps.course, files, link)),
      deleteImmersiveBackground: () => dispatch(patchCourseDeleteImmersiveBackgroundImage()),
    })
  )
)(EditImmersiveBackground);
